import {
  cloneElement,
  CSSProperties,
  ElementType,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode
} from 'react';
import clsx from 'clsx';
import { get } from 'lodash-es';
import { SIZES_VALUE } from './constants';
import { expandSizeValues } from './utils';

import styles from './index.module.css';

type JustifyOptions =
  | 'center'
  | 'left'
  | 'right'
  | 'around'
  | 'between'
  | 'start'
  | 'end';

type AlignOptions = 'center' | 'start' | 'end' | 'baseline' | 'right';

type DirectionOptions = 'row' | 'column' | 'row-reverse' | 'column-reverse';

interface BoxProps extends HTMLAttributes<HTMLElement> {
  component?: ElementType;
  className?: string;
  top?: keyof typeof SIZES_VALUE;
  bottom?: keyof typeof SIZES_VALUE;
  left?: keyof typeof SIZES_VALUE;
  right?: keyof typeof SIZES_VALUE;
  justify?: JustifyOptions;
  align?: AlignOptions;
  gap?: keyof typeof SIZES_VALUE;
  padding?: string;
  margin?: string;
  direction?: DirectionOptions;
  flex?: string;
  flexGrow?: number | string;
  flexBasis?: number | string;
  flexShrink?: number;
  inject?: boolean;
  danger?: boolean;
  wrap?: boolean;
  order?: number;
  style?: CSSProperties;
  alt?: string;
  src?: string;
  width?: string;
  height?: string;
  children?: ReactNode;
}

export const Box = forwardRef<HTMLElement, BoxProps>(
  (
    {
      component: Component = 'div',
      className = '',
      margin,
      top,
      bottom,
      left,
      right,
      justify,
      align,
      gap,
      padding,
      direction,
      inject,
      flex,
      wrap,
      flexGrow,
      flexBasis,
      flexShrink,
      order,
      style: propStyles,
      children,
      danger,
      ...rest
    },
    ref
  ) => {
    const classes = clsx([
      styles.box,
      className,
      justify && styles[`justify_${justify}`],
      align && styles[`align_${align}`],
      wrap && styles.wrap
    ]);

    const dynamicStyles: CSSProperties = {
      marginTop: top ? get(SIZES_VALUE, top) || top : undefined,
      marginBottom: bottom ? get(SIZES_VALUE, bottom) || bottom : undefined,
      marginLeft: left ? get(SIZES_VALUE, left) || left : undefined,
      marginRight: right ? get(SIZES_VALUE, right) || right : undefined,
      gap: gap ? get(SIZES_VALUE, gap) || gap : undefined,
      flexDirection: direction,
      order,
      flex
    };

    if (flexGrow !== undefined) {
      dynamicStyles.flexGrow = flexGrow;
    }
    if (flexBasis !== undefined) {
      dynamicStyles.flexBasis = flexBasis;
    }
    if (flexShrink !== undefined) {
      dynamicStyles.flexShrink = flexShrink;
    }
    if (padding) {
      dynamicStyles.padding = expandSizeValues(padding);
    }
    if (margin) {
      dynamicStyles.margin = expandSizeValues(margin);
    }

    const inlineStyles = { ...dynamicStyles, ...propStyles };

    if (inject && !Array.isArray(children)) {
      return cloneElement(children as ReactElement, {
        className: classes,
        style: inlineStyles
      });
    }

    if (danger) {
      return (
        <Component
          {...rest}
          ref={ref}
          dangerouslySetInnerHTML={{ __html: children as string }}
          className={classes}
          style={inlineStyles}
        />
      );
    }

    return (
      <Component className={classes} style={inlineStyles} ref={ref} {...rest}>
        {children}
      </Component>
    );
  }
);

Box.displayName = 'Box';
